import type { LucideIcon } from 'lucide-react';
import { CircleCheck, CircleX, Info, TriangleAlert, X } from 'lucide-react';
import {
  type CSSProperties,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from './Button';
import { cx } from './cx';

const TONE_ICON: Record<'info' | 'success' | 'warning' | 'danger', LucideIcon> = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleX,
};

/** A message about the page someone is on. */
export function Notice({
  tone = 'info',
  title,
  action,
  className,
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: ReactNode;
  action?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  const Icon = TONE_ICON[tone];
  return (
    <div
      className={cx('mz-notice', `mz-notice-${tone}`, className)}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <Icon size={16} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />
      <div className="mz-notice-body">
        {title != null && <p className="mz-notice-title">{title}</p>}
        {children != null && <div className="mz-notice-text">{children}</div>}
      </div>
      {action}
    </div>
  );
}

/** What a list or page shows before it has anything. */
export function EmptyState({
  icon: Icon,
  title,
  action,
  className,
  children,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  action?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cx('mz-empty', className)}>
      {Icon && (
        <div className="mz-empty-icon">
          <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
        </div>
      )}
      <h3>{title}</h3>
      {children != null && <p>{children}</p>}
      {action != null && <div className="mz-empty-action">{action}</div>}
    </div>
  );
}

/** A grey shape where content is loading; `lines` draws a paragraph. */
export function Skeleton({
  width = '100%',
  height = 12,
  circle,
  lines,
  className,
}: {
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  circle?: boolean;
  lines?: number;
  className?: string;
}) {
  if (lines) {
    return (
      <div className={cx('mz-stack', className)} style={{ gap: 8 }} aria-hidden="true">
        {Array.from({ length: lines }, (_, i) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: identical placeholder lines
            key={i}
            className="mz-skeleton"
            style={{ height: 12, width: i === lines - 1 ? '60%' : '100%' }}
          />
        ))}
      </div>
    );
  }
  return (
    <span
      className={cx('mz-skeleton', className)}
      aria-hidden="true"
      style={{ width, height, borderRadius: circle ? 9999 : undefined }}
    />
  );
}

export interface ToastMessage {
  tone?: 'success' | 'danger' | 'info';
  title: ReactNode;
  text?: ReactNode;
}

/** The result of something someone just did; it goes after a few seconds. */
export function Toast({
  tone = 'success',
  title,
  action,
  onClose,
  children,
}: {
  tone?: 'success' | 'danger' | 'info';
  title: ReactNode;
  action?: ReactNode;
  onClose?: () => void;
  children?: ReactNode;
}) {
  const Icon = TONE_ICON[tone];
  return (
    <div className={cx('mz-toast', `mz-toast-${tone}`)} role="status">
      <Icon size={16} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />
      <div className="mz-notice-body">
        <p className="mz-notice-title">{title}</p>
        {children != null && <p className="mz-notice-text">{children}</p>}
      </div>
      {action}
      {onClose && <Button variant="ghost" size="sm" icon={X} label="Close" onClick={onClose} />}
    </div>
  );
}

const ToastContext = createContext<(message: ToastMessage) => void>(() => undefined);

/** Shows toasts from `useToast()` at the bottom of the screen, one at a time. */
export function ToastProvider({
  duration = 4000,
  children,
}: {
  duration?: number;
  children: ReactNode;
}) {
  const [current, setCurrent] = useState<(ToastMessage & { id: number }) | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const show = useCallback(
    (message: ToastMessage) => {
      clearTimeout(timer.current);
      setCurrent({ ...message, id: Date.now() });
      timer.current = setTimeout(() => setCurrent(null), duration);
    },
    [duration],
  );
  useEffect(() => () => clearTimeout(timer.current), []);
  const value = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        style={{
          position: 'fixed',
          bottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          padding: '0 16px',
          zIndex: 60,
          pointerEvents: 'none',
        }}
      >
        {current && (
          <div style={{ pointerEvents: 'auto' }} key={current.id}>
            <Toast tone={current.tone} title={current.title} onClose={() => setCurrent(null)}>
              {current.text}
            </Toast>
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
